import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowRight, Inbox } from "lucide-react";
import { api } from "../lib/api";
import {useFeatureFlags} from '../contexts/FeatureFlagContext';

/**
 * "Active Services" — surfaces every in-progress item across the platform in
 * one card on the Dashboard. Pulls from /api/me/active-services (Round 52).
 *
 * Empty state: a tidy "Nothing in progress" placeholder so the slot doesn't
 * vanish entirely on a fresh account.
 */
export default function ActiveServices() {
  const {isEnabled}=useFeatureFlags();
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/me/active-services")
      .then((r) => !cancelled && setItems(r.data || []))
      .catch(() => !cancelled && setItems([]));
    load();
    const t = setInterval(load, 30000);   // refresh every 30 s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const visibleItems=items?.filter(it=>isEnabled('nav.'+it.href));
  return (
    <section className="surface rounded-2xl p-5" data-testid="active-services">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-[#10B981]/15 text-[#10B981] flex items-center justify-center">
            <Activity size={14} />
          </span>
          <h2 className="font-display font-bold text-sm sm:text-base">Active Services</h2>
        </div>
        {visibleItems && visibleItems.length > 0 && (
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">{visibleItems.length} in progress</span>
        )}
      </header>

      {items === null && (
        <div className="text-xs text-[#9CA3D4] animate-pulse py-3">Loading your active items…</div>
      )}

      {visibleItems && visibleItems.length === 0 && (
        <div className="flex items-center gap-3 py-3 text-[#9CA3D4]" data-testid="active-services-empty">
          <Inbox size={28} className="text-[#7C3AED]/50" />
          <div>
            <div className="text-xs font-semibold text-white">All caught up</div>
            <div className="text-[11px]">Nothing in progress right now.</div>
          </div>
        </div>
      )}

      {visibleItems && visibleItems.length > 0 && (
        <ul className="divide-y divide-[#26263A]" data-testid="active-services-list">
          {visibleItems.map((it) => (
            <li key={`${it.kind}-${it.id}`}>
              <button
                onClick={() => nav(it.href || "/app")}
                data-testid={`active-service-${it.kind}-${it.id}`}
                className="w-full text-left flex items-center gap-3 py-3 hover:bg-white/[0.025] rounded-lg px-2 -mx-2 transition-colors"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: it.status_color || "#A78BFA", boxShadow: `0 0 8px ${it.status_color || "#A78BFA"}` }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">{it.label}</div>
                  <div className="text-[11px] text-[#9CA3D4]" style={{ color: it.status_color || undefined }}>{it.status}</div>
                </div>
                <ArrowRight size={14} className="text-[#A78BFA] flex-shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
